import React from 'react';

import { ActionIcon, createStyles, Group, Paper, Title, Tooltip, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconFilterOff } from '@tabler/icons-react';
import DownloadCSV from 'components/DownloadCSV';
import { InvoicesTableSearchFilters } from 'components/invoices/InvoicesTable';
import useInvoices from 'hooks/invoice/useInvoices';
import { InvoiceFilterSource } from 'hooks/mixpanel/useStaffTracking';
import { formatDateForApi } from 'utils/date-functions';

import { csvOptions, dataToRow } from './csv';
import InvoiceFilterControls, { InvoiceFilterKey } from './InvoiceFilterControls';

export interface InvoiceFilterBarProps {
  title: string;
  /**
   * `page` — the bar owns the top of a route: gray panel with a bottom border.
   * `embedded` — the bar sits inside a tab that already has its own chrome
   * (athlete detail, package detail), so it draws no background or border.
   */
  variant?: 'page' | 'embedded';
  /** Heading level: 1 for a route, 2 or 3 when nested under a page title. */
  titleOrder?: 1 | 2 | 3;
  /** Controls to render, in display order. Payment Method conventionally goes last. */
  filters: InvoiceFilterKey[];
  searchFilters: InvoicesTableSearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<InvoicesTableSearchFilters>>;
  initialSearchFiltersValue: InvoicesTableSearchFilters;
  orderingValue?: string;
  /** CSV export button. Omit `csvReportType` to hide it (mobile, package tab). */
  csvReportType?: string;
  /** Rendered before the title — the reports' back button. */
  leftSection?: React.ReactNode;
  /** Rendered beside the title — the reports' filter-aware $ total. */
  titleSuffix?: React.ReactNode;
  /** data-cy on the title group; the e2e suite pins two of these. */
  titleDataCy?: string;
  searchPlaceholder?: string;
  /** Which view this bar belongs to — reported with every filter event. */
  source: InvoiceFilterSource;
  /** Extra buttons in the actions group — Upcoming Revenue's projection drawer. */
  extraActions?: React.ReactNode;
  /** DownloadCSV's own date range, which only upcoming-revenue supplies. */
  csvStartDate?: string | null;
  csvEndDate?: string | null;
  dueDateMinDate?: Date;
  dueDateClearable?: boolean;
}

/**
 * The filter bar every desktop invoice view mounts.
 *
 * Responsive behaviour is defined here, once. The bars this replaces each
 * hand-authored `grid-template-areas` for four breakpoints, so any filter added
 * to one view had to be laid out again in every other; a wrapping flex row
 * absorbs whatever subset a view configures at any width instead.
 *
 * The staff mobile view renders `InvoiceFilterControls` directly inside its
 * bottom drawer — same filter set, different interaction.
 */
const useStyles = createStyles((theme, { variant }: { variant: 'page' | 'embedded' }) => ({
  header: {
    padding:
      variant === 'embedded'
        ? `${theme.spacing.xl} ${theme.spacing.xl} ${theme.spacing.xs}`
        : theme.spacing.lg,
    background: variant === 'embedded' ? 'transparent' : theme.colors.gray[0],
    borderRadius: 0,
    borderBottom: variant === 'embedded' ? 'none' : `1px solid ${theme.colors.gray[3]}`,
    // Sizes the bar against this box, not the window: it also renders inside
    // the package panel, which is far narrower. Has to sit on an ancestor —
    // an element cannot answer its own container query.
    containerType: 'inline-size',
    // `container-type` implies `contain: inline-size`, so this box can no
    // longer be sized by its contents. The staff mobile shell lays its page
    // out as a centred flex column, where that was the only thing giving it
    // width — without this it collapses to its padding.
    width: '100%',
    [theme.fn.smallerThan('sm')]: {
      padding: theme.spacing.sm,
    },
  },
  bar: {
    display: 'grid',
    gap: theme.spacing.sm,
    // Title on its own row, filters underneath, at every width. No single-row
    // step: six controls need ~1525px before their placeholders stop being cut.
    gridTemplateAreas: `"head" "filters" "extra"`,
    '@container (min-width: 75em)': {
      gridTemplateColumns: '1fr auto',
      gridTemplateAreas: `"head extra" "filters filters"`,
    },
  },
  head: {
    gridArea: 'head',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    minWidth: 0,
    // On a phone the back button, title, headline total and icons cannot all
    // hold their width, so the row is allowed to break rather than overlap.
    [theme.fn.smallerThan('sm')]: {
      flexWrap: 'wrap',
    },
  },
  filters: {
    gridArea: 'filters',
    display: 'grid',
    gap: theme.spacing.sm,
    // Declared column counts, so every width gets a layout someone chose.
    gridTemplateColumns: '1fr',
    minWidth: 0,
    '@container (min-width: 40em)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    '@container (min-width: 62em)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
    '@container (min-width: 75em)': { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' },
    // Grid items default to min-width:auto, which would keep them at their
    // content width and overflow the track.
    '& > *': { minWidth: 0 },
  },
  extra: {
    gridArea: 'extra',
    '@container (min-width: 75em)': { justifySelf: 'end' },
  },
  title: {
    minWidth: 0,
  },
  download: {
    // Hidden on a phone, as it was in the four report headers.
    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },
  actions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    // Never let the icons shrink; noWrap on the Group keeps them on one line.
    flex: '0 0 auto',
  },
}));

const InvoiceFilterBar = ({
  title,
  variant = 'page',
  titleOrder = 1,
  filters,
  searchFilters,
  setSearchFilters,
  initialSearchFiltersValue,
  orderingValue,
  csvReportType,
  leftSection,
  titleSuffix,
  titleDataCy,
  searchPlaceholder,
  source,
  extraActions,
  csvStartDate,
  csvEndDate,
  dueDateMinDate,
  dueDateClearable,
}: InvoiceFilterBarProps): JSX.Element => {
  const { classes } = useStyles({ variant });
  const theme = useMantineTheme();
  // The report headers this replaces rendered their title smaller on a phone.
  // Left at h1 it wraps onto two lines and pushes the headline total off screen.
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`, true);

  return (
    <Paper className={classes.header} data-cy='invoiceFilterBar'>
      <div className={classes.bar}>
        <div className={classes.head}>
          {leftSection}

          <Group noWrap spacing='xl' className={classes.title} data-cy={titleDataCy}>
            <Title order={titleOrder} size={isDesktop ? `h${titleOrder}` : 'h3'}>
              {title}
            </Title>
            {titleSuffix}
          </Group>

          <Group noWrap className={classes.actions}>
            <Tooltip label='Clear filters'>
              <ActionIcon
                onClick={() => setSearchFilters(initialSearchFiltersValue)}
                data-cy='clearInvoiceFiltersButton'
              >
                <IconFilterOff />
              </ActionIcon>
            </Tooltip>

            {!!csvReportType && (
              <DownloadCSV
                useList={useInvoices}
                dataToRow={dataToRow}
                csvOptions={csvOptions}
                hookOptions={{
                  ...searchFilters,
                  dueDateStart: formatDateForApi(searchFilters.dueDateRange?.[0]),
                  dueDateEnd: formatDateForApi(searchFilters.dueDateRange?.[1]),
                  paymentDateStart: formatDateForApi(searchFilters.paymentDateRange?.[0]),
                  paymentDateEnd: formatDateForApi(searchFilters.paymentDateRange?.[1]),
                  ordering: orderingValue,
                }}
                className={classes.download}
                reportType={csvReportType}
                startDate={csvStartDate}
                endDate={csvEndDate}
              />
            )}
          </Group>
        </div>

        <div className={classes.filters}>
          <InvoiceFilterControls
            filters={filters}
            searchFilters={searchFilters}
            setSearchFilters={setSearchFilters}
            searchPlaceholder={searchPlaceholder}
            source={source}
            dueDateMinDate={dueDateMinDate}
            dueDateClearable={dueDateClearable}
          />
        </div>

        {/* Its own row until xl, where it joins the title line on the right —
            the choreography the report headers this replaces already had. */}
        {!!extraActions && <div className={classes.extra}>{extraActions}</div>}
      </div>
    </Paper>
  );
};

export default InvoiceFilterBar;
